import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import {
  Box,
  Container,
  Paper,
  Tabs,
  Tab,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  CardActions,
  Stack,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Contacts as ContactsIcon,
  CloudUpload,
  PersonAdd,
  ListAlt,
  Add,
  Notifications,
  Refresh,
} from '@mui/icons-material';
import ContactUploader from '../../../elements/seller/ContactUploader';
import ContactList from '../../../elements/seller/ContactList';
import ContactModal from '../../../elements/seller/ContactModal';
import ContactListDialog from '../../../elements/seller/ContactListDialog';
import SendNotificationDialog from '../../../elements/seller/SendNotificationDialog';
import * as Actions from 'actions';

const SellerContacts = (props) => {
  const { getContacts, getContactLists, contactLists = { lists: [] } } = props;
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [activeTab, setActiveTab] = useState(0);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [showCreateListDialog, setShowCreateListDialog] = useState(false);
  const [showNotificationDialog, setShowNotificationDialog] = useState(false);
  const [selectedList, setSelectedList] = useState(null);

  useEffect(() => {
    // Load contacts on mount
    getContacts({ search: "", skip: 0, limit: 50 });
  }, [getContacts]);

  useEffect(() => {
    // Load contact lists when switching to that tab
    if (activeTab === 2) {
      getContactLists({});
    }
  }, [activeTab, getContactLists]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleOpenAddModal = () => {
    setShowAddModal(true);
    setEditingContact(null);
  };

  const handleCloseModal = () => {
    setShowAddModal(false);
    setEditingContact(null);
  };

  const handleEdit = (contact) => {
    setShowAddModal(true);
    setEditingContact(contact);
  };

  const handleUploadSuccess = () => {
    // Refresh contacts list after successful upload
    getContacts({ search: "", skip: 0, limit: 50 });
    // Switch to contacts tab
    setActiveTab(0);
  };

  const handleOpenCreateListDialog = () => {
    setShowCreateListDialog(true);
  };

  const handleCloseCreateListDialog = () => {
    setShowCreateListDialog(false);
    // Refresh lists after creating
    getContactLists({});
  };

  const handleSendNotificationToList = (list) => {
    setSelectedList(list);
    setShowNotificationDialog(true);
  };

  const handleCloseNotificationDialog = () => {
    setShowNotificationDialog(false);
    setSelectedList(null);
  };

  const handleRefreshLists = () => {
    getContactLists({});
  };

  return (
    <Container maxWidth="xl" sx={{ mt: { xs: 2, sm: 4 }, mb: { xs: 2, sm: 4 }, px: { xs: 1, sm: 2 } }}>
      <Box sx={{ 
        mb: { xs: 2, sm: 3 }, 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'stretch', sm: 'center' },
        gap: { xs: 1, sm: 0 }
      }}>
        <Box>
          <Typography variant={isMobile ? "h5" : "h4"} gutterBottom>
            Contacts
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage your customer contacts and list
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="primary"
          startIcon={!isMobile && <PersonAdd />}
          onClick={handleOpenAddModal}
          size={isMobile ? "small" : "medium"}
          fullWidth={isMobile}
          sx={{ mt: { xs: 1, sm: 0 } }}
        >
          {isMobile ? <><PersonAdd sx={{ mr: 0.5 }} /> Add</> : "Add Contact"}
        </Button>
      </Box>

      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        {/* Tabs */}
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant={isMobile ? "scrollable" : "fullWidth"}
          scrollButtons={isMobile ? "auto" : false}
          sx={{ borderBottom: 1, borderColor: 'divider', minHeight: { xs: 48, sm: 64 } }}
        >
          <Tab
            icon={isMobile ? <ContactsIcon /> : <ContactsIcon />}
            label={isMobile ? "Contacts" : "All Contacts"}
            iconPosition="start"
            sx={{ minHeight: { xs: 48, sm: 64 }, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
          />
          <Tab
            icon={<CloudUpload />}
            label="Upload"
            iconPosition="start"
            sx={{ minHeight: { xs: 48, sm: 64 }, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
          />
          <Tab
            icon={<ListAlt />}
            label={isMobile ? "Lists" : "Contact Lists"}
            iconPosition="start"
            sx={{ minHeight: { xs: 48, sm: 64 }, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
          />
        </Tabs>

        {/* Tab Panels */}
        <Box sx={{ p: { xs: 0, sm: 3 } }}>
          {/* All Contacts Tab */}
          {activeTab === 0 && (
            <ContactList onEdit={handleEdit} />
          )}

          {/* Upload Tab */}
          {activeTab === 1 && (
            <ContactUploader onUploadSuccess={handleUploadSuccess} />
          )}

          {/* Contact Lists Tab */}
          {activeTab === 2 && (
            <Box sx={{ p: { xs: 2, sm: 0 } }}>
              <Box sx={{ 
                mb: 2, 
                display: 'flex', 
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between', 
                alignItems: { xs: 'stretch', sm: 'center' },
                gap: { xs: 1, sm: 0 }
              }}>
                <Typography variant={isMobile ? "subtitle1" : "h6"}>
                  Contact Lists ({contactLists.lists.length})
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                  <Button
                    variant="outlined"
                    startIcon={!isMobile && <Refresh />}
                    onClick={handleRefreshLists}
                    size={isMobile ? "small" : "medium"}
                    fullWidth={isMobile}
                  >
                    {isMobile ? <><Refresh sx={{ mr: 0.5 }} /> Refresh</> : "Refresh"}
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={!isMobile && <Add />}
                    onClick={handleOpenCreateListDialog}
                    size={isMobile ? "small" : "medium"}
                    fullWidth={isMobile}
                  >
                    {isMobile ? <><Add sx={{ mr: 0.5 }} /> Create</> : "Create List"}
                  </Button>
                </Stack>
              </Box>

              {contactLists.lists.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: { xs: 4, sm: 8 } }}>
                  <ListAlt sx={{ fontSize: { xs: 48, sm: 64 }, color: 'text.secondary', mb: 2 }} />
                  <Typography variant={isMobile ? "body1" : "h6"} color="text.secondary" gutterBottom>
                    No Contact Lists Yet
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3, px: { xs: 2, sm: 0 } }}>
                    Create contact lists to organize and send notifications to groups of contacts
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={handleOpenCreateListDialog}
                    size={isMobile ? "small" : "medium"}
                  >
                    Create Your First List
                  </Button>
                </Box>
              ) : (
                <>
                  {/* Mobile Card View */}
                  {isMobile ? (
                    <Stack spacing={1.5}>
                      {contactLists.lists.map((list) => (
                        <Card key={list._id} variant="outlined">
                          <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant="body1" fontWeight="medium" noWrap>
                                  {list.name}
                                </Typography>
                              </Box>
                              <Chip
                                label={list.contact_count || 0}
                                color="info"
                                size="small"
                                sx={{ ml: 1 }}
                              />
                            </Box>
                          </CardContent>
                          <CardActions sx={{ justifyContent: 'flex-end', pt: 0 }}>
                            <Button
                              size="small"
                              variant="contained"
                              color="primary"
                              startIcon={<Notifications />}
                              onClick={() => handleSendNotificationToList(list)}
                              disabled={!list.contact_count || list.contact_count === 0}
                              fullWidth
                            >
                              Send Notification
                            </Button>
                          </CardActions>
                        </Card>
                      ))}
                    </Stack>
                  ) : (
                    /* Desktop Table View */
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>List Name</TableCell>
                            <TableCell align="right">Contacts</TableCell>
                            <TableCell align="right">Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {contactLists.lists.map((list) => (
                            <TableRow key={list._id} hover>
                              <TableCell>
                                <Typography variant="body1" fontWeight="medium">
                                  {list.name}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Chip
                                  label={list.contact_count || 0}
                                  color="info"
                                  size="small"
                                />
                              </TableCell>
                              <TableCell align="right">
                                <Tooltip title="Send Notification">
                                  <IconButton
                                    color="primary"
                                    onClick={() => handleSendNotificationToList(list)}
                                    disabled={!list.contact_count || list.contact_count === 0}
                                  >
                                    <Notifications />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </>
              )}
            </Box>
          )}
        </Box>
      </Paper>

      {/* Add/Edit Contact Modal */}
      <ContactModal
        open={showAddModal}
        onClose={handleCloseModal}
        contact={editingContact}
      />

      {/* Create Contact List Dialog */}
      <ContactListDialog
        open={showCreateListDialog}
        onClose={handleCloseCreateListDialog}
      />

      {/* Send Notification Dialog */}
      <SendNotificationDialog
        open={showNotificationDialog}
        onClose={handleCloseNotificationDialog}
        contactList={selectedList}
      />
    </Container>
  );
};

const _SellerContacts = connect(
  ({ sellerContacts }) => ({
    contacts: sellerContacts.contacts,
    contactLists: sellerContacts.contactLists,
  }),
  (dispatch) => bindActionCreators({ ...Actions }, dispatch)
)(SellerContacts);

export { _SellerContacts as default };
